import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import AIVoiceDataTable from './AIVoiceDataTable';
import type { AIVoiceTableRow } from './types';
import { GroupRowType, TableRowType } from './types';

import type { AIVoice } from 'js/src/client';
import { deleteVoiceClone } from 'js/src/components/app/VoiceCloningModal/voice-cloning-operations';
import useAIVoiceFilters from 'js/src/components/app/VoiceSelectionModal/useAIVoiceFilters';
import useAIVoiceSearch from 'js/src/components/app/VoiceSelectionModal/useAIVoiceSearch';
import { toggleAIVoiceFavourite } from 'js/src/data/ai-voices-and-avatars/ai-voice-modal/ai-voice-modal-operations';
import {
    getAIVoiceModalAvailableVoices,
    getAIVoiceModalDeprecatedVoices,
    getAIVoiceModalPrivateVoices,
} from 'js/src/data/ai-voices-and-avatars/ai-voice-modal/ai-voice-modal-selectors';
import { removeAIVoice } from 'js/src/data/ai-voices-and-avatars/ai-voice-modal/ai-voice-modal-slice';
import type { VOICE_SELECTION_MODAL_SOURCE, VoiceSelectionModalContext } from 'js/src/data/modals';
import { getModalContext, hideModal, MODAL_IDS, showModal } from 'js/src/data/modals';
import { useAppDispatch } from 'js/src/libs/RootState';

type VoiceSelectionModalResultsProps = {
    className?: string;
    selectedVoice: AIVoice | null;
    onVoiceSelected: (voice: AIVoice) => void;
    canUsePremiumAIVoices: boolean;
    source: VOICE_SELECTION_MODAL_SOURCE | null;
};

const VoiceSelectionModalResults: React.FC<VoiceSelectionModalResultsProps> = ({
    className,
    selectedVoice,
    onVoiceSelected,
    canUsePremiumAIVoices,
    source,
}) => {
    const dispatch = useAppDispatch();
    const containerRef = useRef<HTMLDivElement>(null);
    const audioElementRefs = useRef<Record<string, HTMLAudioElement>>({});
    const fallbackPreviewUrls = useRef<Record<string, string | null>>({});
    const [previewingSlug, setPreviewingSlug] = useState<string | null>(null);
    // The slug the user currently wants playing, read synchronously by listeners
    // that fire after a render (`previewingSlug` would be stale in their closure).
    const previewingSlugRef = useRef<string | null>(null);
    const detachPreviewListeners = useRef<(() => void) | null>(null);

    const allPrivateVoices = useSelector(getAIVoiceModalPrivateVoices);
    const allAvailableVoices = useSelector(getAIVoiceModalAvailableVoices);
    const allDeprecatedVoices = useSelector(getAIVoiceModalDeprecatedVoices);

    const { isUninitialized, isFetching, hasNextPage, triggerFetch, selectedLanguage } =
        useAIVoiceSearch();
    const { languageFilterOptions } = useAIVoiceFilters();

    const selectedLanguageDisplay = useMemo(
        () =>
            languageFilterOptions.find((option) => option.value === selectedLanguage)?.display ??
            null,
        [languageFilterOptions, selectedLanguage],
    );

    useEffect(() => {
        if (!isUninitialized) {
            return;
        }
        triggerFetch();
    }, [isUninitialized, triggerFetch]);

    const fetchNextPage = useCallback(() => {
        if (!hasNextPage) {
            return;
        }
        if (isFetching) {
            return;
        }
        triggerFetch();
    }, [hasNextPage, isFetching, triggerFetch]);

    const onToggleFavourite = useCallback(
        (slug: string) => {
            dispatch(toggleAIVoiceFavourite(slug));
        },
        [dispatch],
    );

    const registerAudioElement = useCallback(
        (slug: string, el: HTMLAudioElement, fallbackUrl: string | null) => {
            audioElementRefs.current[slug] = el;
            fallbackPreviewUrls.current[slug] = fallbackUrl;
        },
        [],
    );

    const stopPreview = useCallback(() => {
        detachPreviewListeners.current?.();
        detachPreviewListeners.current = null;
        previewingSlugRef.current = null;
        setPreviewingSlug((currentSlug) => {
            if (currentSlug) {
                const audio = audioElementRefs.current[currentSlug];
                if (audio) {
                    audio.pause();
                    audio.currentTime = 0;
                }
            }
            return null;
        });
    }, []);

    const startPreview = useCallback(
        (slug: string) => {
            stopPreview();
            const audio = audioElementRefs.current[slug];
            if (!audio) return;

            const clearPreviewing = () => {
                if (previewingSlugRef.current === slug) {
                    previewingSlugRef.current = null;
                }
                setPreviewingSlug((current) => (current === slug ? null : current));
            };

            // Language previews are short-lived provider URLs that stop resolving. An
            // expired one answers with an XML error body rather than audio, so the
            // failure can surface either as a rejected play() or as an `error` event
            // on the element once it fails to decode — recover from both.
            const playDefaultPreview = () => {
                // A request the user has already stopped or switched away from can still
                // fail afterwards; recovering from it would start audio on a row that
                // shows ▶ and that stopPreview() no longer reaches.
                if (previewingSlugRef.current !== slug) return;
                const fallbackUrl = fallbackPreviewUrls.current[slug];
                const alreadyOnFallback =
                    !!fallbackUrl && audio.src === new URL(fallbackUrl, window.location.href).href;
                if (!fallbackUrl || alreadyOnFallback) {
                    clearPreviewing();
                    return;
                }
                audio.src = fallbackUrl;
                audio.play().catch(clearPreviewing);
            };

            // Scoped to this preview: a listener left attached would fire for a
            // request the user has moved on from, and stack up across previews.
            audio.addEventListener('ended', clearPreviewing);
            audio.addEventListener('error', playDefaultPreview);
            detachPreviewListeners.current = () => {
                audio.removeEventListener('ended', clearPreviewing);
                audio.removeEventListener('error', playDefaultPreview);
            };

            previewingSlugRef.current = slug;
            audio.play().catch((error: DOMException) => {
                // AbortError means a pause() or a new src interrupted playback — stopping,
                // switching voices, changing the language filter. Not a load failure.
                if (error?.name === 'AbortError') return;
                playDefaultPreview();
            });
            setPreviewingSlug(slug);
        },
        [stopPreview],
    );

    const voiceSelectionContext =
        useSelector(getModalContext<VoiceSelectionModalContext>) ?? undefined;

    const onEditVoice = useCallback(
        (slug: string) => {
            dispatch(hideModal());
            dispatch(
                showModal({
                    modal_id: MODAL_IDS.VOICE_EDIT,
                    modal_context: {
                        voiceSlug: slug,
                        returnToVoiceSelection: voiceSelectionContext,
                    },
                }),
            );
        },
        [dispatch, voiceSelectionContext],
    );

    const onDeleteVoice = useCallback(
        async (slug: string) => {
            const success = await deleteVoiceClone(slug);
            if (success) {
                dispatch(removeAIVoice(slug));
            }
        },
        [dispatch],
    );

    const tableData = useMemo(() => {
        const rows: AIVoiceTableRow[] = [];

        rows.push({ type: TableRowType.AddYourOwn });

        if (allPrivateVoices.length > 0) {
            rows.push({
                type: TableRowType.Group,
                id: GroupRowType.Private,
                title: 'Your brand voices',
            });
            allPrivateVoices.forEach((v) => rows.push({ type: TableRowType.Voice, voice: v }));
        }

        if (allAvailableVoices.length > 0 || allDeprecatedVoices.length > 0) {
            rows.push({
                type: TableRowType.Group,
                id: GroupRowType.Public,
                title: 'Lumen5 voices',
            });
            allAvailableVoices.forEach((v) => rows.push({ type: TableRowType.Voice, voice: v }));
            allDeprecatedVoices.forEach((v) => rows.push({ type: TableRowType.Voice, voice: v }));
        }

        return rows;
    }, [allPrivateVoices, allAvailableVoices, allDeprecatedVoices]);

    return (
        <div className={classNames(className)} ref={containerRef}>
            <AIVoiceDataTable
                tableData={tableData}
                isFetching={isFetching}
                fetchNextPage={fetchNextPage}
                selectedVoice={selectedVoice}
                onVoiceSelected={onVoiceSelected}
                previewingSlug={previewingSlug}
                stopPreview={stopPreview}
                startPreview={startPreview}
                canUsePremiumAIVoices={canUsePremiumAIVoices}
                registerAudioElement={registerAudioElement}
                containerRef={containerRef}
                source={source}
                onToggleFavourite={onToggleFavourite}
                onEditVoice={onEditVoice}
                onDeleteVoice={onDeleteVoice}
                selectedLanguage={selectedLanguage}
                selectedLanguageDisplay={selectedLanguageDisplay}
            />
        </div>
    );
};

export default VoiceSelectionModalResults;
